import React from 'react';
import { View, Text } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { NumberStepperInput } from './NumberStepperInput';
import { ItemWeeksConfig } from '../types';
import { mwProblem, mwValid } from '../utils';
import { styles } from '../PeopleHubConfiguration.styles';

interface OnboardingGlidepathCardProps {
  onboardingWeeks: number;
  milestoneWeeks: [number, number, number];
  onMilestoneWeekChange: (index: 0 | 1 | 2, week: number) => void;
  itemWeeks: ItemWeeksConfig;
  onItemWeekChange: (key: keyof ItemWeeksConfig, week: number) => void;
}

export const OnboardingGlidepathCard: React.FC<OnboardingGlidepathCardProps> = ({
  onboardingWeeks,
  milestoneWeeks,
  onMilestoneWeekChange,
  itemWeeks,
  onItemWeekChange,
}) => {
  const errorMessage = mwProblem(milestoneWeeks, onboardingWeeks);
  const isValid = mwValid(milestoneWeeks, onboardingWeeks);

  const weekOptions = Array.from({ length: onboardingWeeks }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  return (
    <View style={[styles.card, styles.cardPad]}>
      {/* Header */}
      <View style={styles.cardHeader}>
        <View style={styles.cardTitleGroup}>
          <Text style={styles.cardTitle}>Onboarding Glidepath</Text>
          <Text style={styles.cardSubtitle}>
            Which onboarding week each milestone, the preferred method review, training, and
            certification fall in. Due dates on the queue and the profile are the hire date plus
            that week.
          </Text>
        </View>
        <View style={isValid ? styles.badgeInOrder : styles.badgeNeedsAttention}>
          <Text style={isValid ? styles.badgeInOrderText : styles.badgeNeedsAttentionText}>
            {isValid ? 'In order' : 'Needs attention'}
          </Text>
        </View>
      </View>

      {/* Row 1: Milestones (3 columns) */}
      <View style={styles.glidepathRow}>
        {/* Milestone 1 */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Milestone 1 week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={milestoneWeeks[0]}
              onChange={(val) => onMilestoneWeekChange(0, val)}
              min={1}
              max={onboardingWeeks}
              isInvalid={!isValid}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>After the three orientation weeks</Text>
        </View>

        {/* Milestone 2 */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Milestone 2 week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={milestoneWeeks[1]}
              onChange={(val) => onMilestoneWeekChange(1, val)}
              min={1}
              max={onboardingWeeks}
              isInvalid={!isValid}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>After Milestone 1</Text>
        </View>

        {/* Milestone 3 */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Milestone 3 week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={milestoneWeeks[2]}
              onChange={(val) => onMilestoneWeekChange(2, val)}
              min={1}
              max={onboardingWeeks}
              isInvalid={!isValid}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>Usually the last week of the glidepath</Text>
        </View>
      </View>

      {/* Validation Message or Note */}
      {errorMessage ? (
        <View style={styles.warningBox}>
          <MaterialIcons name="error-outline" size={18} color="#814705" />
          <Text style={styles.warningText}>{errorMessage}</Text>
        </View>
      ) : (
        <Text style={styles.guidanceText}>
          Milestones must stay in sequence. The glidepath below marks each milestone week.
        </Text>
      )}

      {/* Row 2: Activities (3 columns) */}
      <View style={[styles.glidepathRow, { marginTop: 12 }]}>
        {/* Preferred Method */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Preferred Method week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={itemWeeks.preferred}
              onChange={(val) => onItemWeekChange('preferred', val)}
              min={1}
              max={onboardingWeeks}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>Preferred methods review with the employee</Text>
        </View>

        {/* Training */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Training week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={itemWeeks.training}
              onChange={(val) => onItemWeekChange('training', val)}
              min={1}
              max={onboardingWeeks}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>Required training sign off</Text>
        </View>

        {/* Certification */}
        <View style={styles.glidepathCol}>
          <Text style={styles.formLabel}>Certification week</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={itemWeeks.certification}
              onChange={(val) => onItemWeekChange('certification', val)}
              min={1}
              max={onboardingWeeks}
            />
            <Text style={styles.suffixText}>of {onboardingWeeks}</Text>
          </View>
          <Text style={styles.formHint}>PIT and role certifications</Text>
        </View>
      </View>
    </View>
  );
};
